import { useState } from 'react';

import { Button, TabPage, TabView } from 'fluxo-ui';
import { CheckIcon, DownloadIcon, EyeIcon, MinusIcon, PrintIcon, TimesIcon } from 'fluxo-ui/icons';

interface QuestionAnswer {
    questionId: string;
    questionNumber: number;
    questionTitle: string;
    questionType: string;
    questionContent: string;
    correctAnswer: any;
    studentAnswer: any;
    isCorrect: boolean | 'partial';
    marksAwarded: number;
    maxMarks: number;
    timeSpentSeconds: number;
    gradingMethod: 'auto' | 'ai' | 'manual';
    feedback?: string;
}

interface IndividualResultProps {
    attempt: {
        id: string;
        attemptId: string;
        examTitle: string;
        studentName: string;
        studentEmail: string;
        attemptNumber: number;
        startTime: string;
        endTime: string;
        timeElapsedSeconds: number;
        status: string;
        scoredMarks: number;
        totalMarks: number;
        percentage: number;
        passed: boolean;
        gradingStatus: string;
    };
    answers: QuestionAnswer[];
    onDownload?: () => Promise<void>;
    onPrint?: () => void;
}

const IndividualResult: React.FC<IndividualResultProps> = ({ attempt, answers, onDownload, onPrint }) => {
    const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set());
    const [downloading, setDownloading] = useState(false);

    const toggleQuestion = (questionId: string) => {
        const newExpanded = new Set(expandedQuestions);
        if (newExpanded.has(questionId)) {
            newExpanded.delete(questionId);
        } else {
            newExpanded.add(questionId);
        }
        setExpandedQuestions(newExpanded);
    };

    const handleDownload = async () => {
        setDownloading(true);
        try {
            await onDownload?.();
        } finally {
            setDownloading(false);
        }
    };

    const formatTime = (seconds: number) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        if (hours > 0) return `${hours}h ${minutes}m ${secs}s`;
        if (minutes > 0) return `${minutes}m ${secs}s`;
        return `${secs}s`;
    };

    const getScoreColor = (percentage: number) => {
        if (percentage >= 80) return 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20';
        if (percentage >= 60) return 'text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20';
        return 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20';
    };

    const getCorrectnessIcon = (isCorrect: boolean | 'partial') => {
        if (isCorrect === true) {
            return <CheckIcon className="w-5 h-5 text-green-600 dark:text-green-400" />;
        }
        if (isCorrect === 'partial') {
            return <MinusIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />;
        }
        return <TimesIcon className="w-5 h-5 text-red-600 dark:text-red-400" />;
    };

    const getCorrectnessLabel = (isCorrect: boolean | 'partial') => {
        if (isCorrect === true) return 'Correct';
        if (isCorrect === 'partial') return 'Partially Correct';
        return 'Incorrect';
    };

    const getCorrectnessColor = (isCorrect: boolean | 'partial') => {
        if (isCorrect === true) return 'text-green-600 dark:text-green-400';
        if (isCorrect === 'partial') return 'text-yellow-600 dark:text-yellow-400';
        return 'text-red-600 dark:text-red-400';
    };

    const stats = {
        correct: answers.filter((a) => a.isCorrect === true).length,
        partial: answers.filter((a) => a.isCorrect === 'partial').length,
        incorrect: answers.filter((a) => a.isCorrect === false).length,
        totalQuestions: answers.length,
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white">{attempt.examTitle}</h2>
                        <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Individual Result Report</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <Button label="Print" leftIcon={PrintIcon} variant="default" layout="outlined" size="sm" onClick={onPrint} />
                        <Button
                            label="Download PDF"
                            leftIcon={DownloadIcon}
                            variant="primary"
                            size="sm"
                            onClick={handleDownload}
                            isLoading={downloading}
                            disabled={downloading}
                        />
                    </div>
                </div>

                {/* Student Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div>
                        <div className="text-sm text-slate-600 dark:text-slate-400">Student</div>
                        <div className="font-medium text-slate-900 dark:text-white">{attempt.studentName}</div>
                        <div className="text-sm text-slate-500 dark:text-slate-400">{attempt.studentEmail}</div>
                    </div>
                    <div>
                        <div className="text-sm text-slate-600 dark:text-slate-400">Attempt Details</div>
                        <div className="font-medium text-slate-900 dark:text-white">Attempt #{attempt.attemptNumber}</div>
                        <div className="text-sm text-slate-500 dark:text-slate-400">ID: {attempt.attemptId}</div>
                    </div>
                    <div>
                        <div className="text-sm text-slate-600 dark:text-slate-400">Submitted At</div>
                        <div className="font-medium text-slate-900 dark:text-white">{new Date(attempt.endTime).toLocaleString()}</div>
                    </div>
                    <div>
                        <div className="text-sm text-slate-600 dark:text-slate-400">Time Taken</div>
                        <div className="font-medium text-slate-900 dark:text-white">{formatTime(attempt.timeElapsedSeconds)}</div>
                    </div>
                </div>

                {/* Score Banner */}
                <div className={`p-6 rounded-lg ${getScoreColor(attempt.percentage)}`}>
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-4xl font-bold mb-2">{attempt.percentage.toFixed(1)}%</div>
                            <div className="text-lg font-medium">
                                {attempt.scoredMarks} / {attempt.totalMarks} marks
                            </div>
                            <div className="text-sm mt-2">
                                {attempt.passed ? (
                                    <span className="font-semibold">✓ Passed</span>
                                ) : (
                                    <span className="font-semibold">✗ Failed</span>
                                )}
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-sm mb-2">Status</div>
                            <div className="px-4 py-2 bg-white/30 rounded-lg">
                                <div className="font-semibold">{attempt.status.replace(/_/g, ' ')}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Statistics */}
            <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Question Statistics</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
                        <div className="text-2xl font-bold text-slate-900 dark:text-white">{stats.totalQuestions}</div>
                        <div className="text-xs text-slate-600 dark:text-slate-400">Total Questions</div>
                    </div>
                    <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                        <div className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.correct}</div>
                        <div className="text-xs text-green-600 dark:text-green-400">Correct</div>
                    </div>
                    <div className="text-center p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                        <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{stats.partial}</div>
                        <div className="text-xs text-yellow-600 dark:text-yellow-400">Partially Correct</div>
                    </div>
                    <div className="text-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                        <div className="text-2xl font-bold text-red-600 dark:text-red-400">{stats.incorrect}</div>
                        <div className="text-xs text-red-600 dark:text-red-400">Incorrect</div>
                    </div>
                </div>
            </div>

            {/* Question-by-Question Review */}
            <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Question-by-Question Review</h3>
                <div className="space-y-3">
                    {answers.map((answer) => (
                        <div key={answer.questionId} className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                            {/* Question Header */}
                            <button
                                onClick={() => toggleQuestion(answer.questionId)}
                                className="w-full p-4 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                            >
                                <div className="flex items-center gap-4 flex-1">
                                    <span className="w-10 h-10 flex items-center justify-center bg-slate-100 dark:bg-slate-700 rounded-full font-semibold text-slate-900 dark:text-white">
                                        {answer.questionNumber}
                                    </span>
                                    <div className="flex-1 text-left">
                                        <div className="font-medium text-slate-900 dark:text-white">{answer.questionTitle}</div>
                                        <div className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                                            {answer.questionType.replace(/_/g, ' ').toUpperCase()}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <div className="text-right">
                                            <div className="text-sm font-medium text-slate-900 dark:text-white">
                                                {answer.marksAwarded} / {answer.maxMarks}
                                            </div>
                                            <div className="text-xs text-slate-500 dark:text-slate-400">marks</div>
                                        </div>
                                        <div className={`flex items-center gap-2 ${getCorrectnessColor(answer.isCorrect)}`}>
                                            {getCorrectnessIcon(answer.isCorrect)}
                                            <span className="text-sm font-medium">{getCorrectnessLabel(answer.isCorrect)}</span>
                                        </div>
                                    </div>
                                </div>
                                <EyeIcon className="w-5 h-5 text-slate-400 ml-4" />
                            </button>

                            {/* Question Details (Expanded) */}
                            {expandedQuestions.has(answer.questionId) && (
                                <div className="p-4 bg-slate-50 dark:bg-slate-700 border-t border-slate-200 dark:border-slate-600">
                                    <TabView>
                                        <TabPage header="Question">
                                            <div className="p-4 space-y-4">
                                                <div>
                                                    <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                                                        Question:
                                                    </h4>
                                                    <div className="text-slate-900 dark:text-white">{answer.questionContent}</div>
                                                </div>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <div className="text-sm text-slate-600 dark:text-slate-400">
                                                        <span className="font-medium">Time Spent:</span>{' '}
                                                        {formatTime(answer.timeSpentSeconds)}
                                                    </div>
                                                    <div className="text-sm text-slate-600 dark:text-slate-400">
                                                        <span className="font-medium">Grading Method:</span>{' '}
                                                        {answer.gradingMethod.toUpperCase()}
                                                    </div>
                                                </div>
                                            </div>
                                        </TabPage>
                                        <TabPage header="Your Answer">
                                            <div className="p-4">
                                                <div className="p-4 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                                                    <div className="text-slate-900 dark:text-white">
                                                        {answer.studentAnswer !== null && answer.studentAnswer !== '' ? (
                                                            String(answer.studentAnswer)
                                                        ) : (
                                                            <span className="text-slate-400 italic">No answer provided</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </TabPage>
                                        <TabPage header="Correct Answer">
                                            <div className="p-4">
                                                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                                                    <div className="text-green-900 dark:text-green-100">{String(answer.correctAnswer)}</div>
                                                </div>
                                            </div>
                                        </TabPage>
                                        {answer.feedback && (
                                            <TabPage header="Feedback">
                                                <div className="p-4">
                                                    <div className="p-4 bg-teal-50 dark:bg-teal-900/20 rounded-lg border border-teal-200 dark:border-teal-800">
                                                        <div className="text-teal-900 dark:text-teal-100">{answer.feedback}</div>
                                                    </div>
                                                </div>
                                            </TabPage>
                                        )}
                                    </TabView>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default IndividualResult;
